import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [activeCompany, setActiveCompany] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Build flat permissions list from roles
  const extractPermissions = (rolesData) => {
    const perms = new Set();
    (rolesData || []).forEach(role => {
      (role.permissions || []).forEach(p => {
        perms.add(`${p.module}:${p.action}`);
      });
    });
    return Array.from(perms);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedCompanies = localStorage.getItem('companies');
    const savedActiveCompany = localStorage.getItem('activeCompanyId');

    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      setIsSuperAdmin(parsed.isSuperAdmin || false);
      setRoles(parsed.roles || []);
      setPermissions(extractPermissions(parsed.roles));
      if (savedCompanies) setCompanies(JSON.parse(savedCompanies));
      if (savedActiveCompany) {
        setActiveCompanyId(parseInt(savedActiveCompany));
        const comps = savedCompanies ? JSON.parse(savedCompanies) : [];
        const active = comps.find(c => c.id === parseInt(savedActiveCompany));
        if (active) setActiveCompany(active);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token, user: userData, companies: comps, activeCompanyId: acId } = res.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('companies', JSON.stringify(comps));
    localStorage.setItem('activeCompanyId', acId);

    setUser(userData);
    setIsSuperAdmin(userData.isSuperAdmin || false);
    setRoles(userData.roles || []);
    setPermissions(extractPermissions(userData.roles));
    setCompanies(comps);
    setActiveCompanyId(acId);
    if (comps.length > 0) {
      const active = comps.find(c => c.id === acId) || comps[0];
      setActiveCompany(active);
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('companies');
    localStorage.removeItem('activeCompanyId');
    setUser(null);
    setCompanies([]);
    setActiveCompanyId(null);
    setActiveCompany(null);
    setIsSuperAdmin(false);
    setRoles([]);
    setPermissions([]);
  };

  const switchCompany = async (companyId) => {
    const res = await authAPI.switchCompany(companyId);
    const { token, activeCompanyId: acId, company } = res.data;

    localStorage.setItem('token', token);
    localStorage.setItem('activeCompanyId', acId);

    setActiveCompanyId(acId);
    setActiveCompany(company);
    return res.data;
  };

  const setDefaultCompany = async (companyId) => {
    await authAPI.setDefaultCompany(companyId);
    // Update companies array to reflect new default
    const updated = companies.map(c => ({
      ...c,
      is_default: c.id === companyId ? true : false,
    }));
    setCompanies(updated);
    localStorage.setItem('companies', JSON.stringify(updated));
  };

  // Check if user has a specific permission (Super Admin bypasses all checks)
  const hasPermission = (module, action) => {
    if (isSuperAdmin) return true;
    return permissions.includes(`${module}:${action}`);
  };

  const value = {
    user,
    companies,
    activeCompanyId,
    activeCompany,
    isSuperAdmin,
    roles,
    permissions,
    loading,
    login,
    logout,
    switchCompany,
    setDefaultCompany,
    setCompanies,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
